import { getActiveCaregiverId, getActiveOfficeId } from '@lib/activeOffice';
import { getCallSessionToken } from '@lib/callSession';
import { logger } from '@/lib/logger';
import { getCallSessionSocketConfig, getDefaulSocketConfig } from '@src/const/socketConfig';
import { useAuthStore } from '@stores/authStore';
import { CallEventType, useCallEventStore } from '@stores/call/callEventStore';
import {
  CallRequest,
  SocketEvent,
  SocketEventResponse,
  type SocketEventType,
} from '@therame/shared';
import { defineStore } from 'pinia';
import { ReplaySubject, Subject } from 'rxjs';
import { Socket, io } from 'socket.io-client';
import { computed, ref } from 'vue';

export const useCallSocketStore = defineStore('callSocket', () => {
  const authStore = useAuthStore();
  const callEventStore = useCallEventStore();

  const socketEndpoint = (import.meta.env.VITE_WS_ROOT as string) + '/calls';
  const socket = ref<Socket | undefined>();
  const callRequests = ref<Map<string, CallRequest>>(new Map<string, CallRequest>());
  const callRequestTrigger = new ReplaySubject<SocketEvent<'call-join-request'>>();
  const callConnectTrigger = new Subject<SocketEvent<'call-approve-join-request'>>();
  const joinTrigger = new Subject<SocketEvent<'call-join'>>();
  const leaveTrigger = new Subject<SocketEvent<'call-leave'>>();
  const callDisconnectTrigger = new Subject<SocketEvent<'call-disconnect'>>();
  const callEndedByTherapistTrigger = new Subject<SocketEvent<'call-ended-by-therapist'>>();
  const callUserGotKickedTrigger = new Subject<SocketEvent<'call-user-got-kicked'>>();
  const fileUploadTrigger = new Subject<void>();
  const callIdInServerStatus = ref<string | null>(null);

  const nextCallRequest = computed(() => {
    const requests = Array.from(callRequests.value.values());
    return Array.from(requests)[0];
  });

  const disconnectSocket = () => {
    socket.value?.disconnect();
  };

  // used to get the current callId the user had connected to previously to show the miniToolBar.
  const status = async (): Promise<SocketEventResponse<'response-call-status'>> => {
    const response = await socket.value?.timeout(3000).emitWithAck<SocketEventType>('call-status');
    if (response.statusCode === 200 && response.body) {
      callIdInServerStatus.value = response.body.callId;
    } else if (response.statusCode === 204 || response.statusCode === 403) {
      callIdInServerStatus.value = null;
    }
    return response;
  };

  const join = async (callId: string): Promise<SocketEventResponse<'response-call-join'>> => {
    return socket.value?.timeout(3000).emitWithAck<SocketEventType>('call-join', {
      props: {
        callId,
      },
    });
  };

  const leave = async (callId: string): Promise<SocketEventResponse<'response-call-leave'>> => {
    const result = await socket.value?.timeout(5000).emitWithAck<SocketEventType>('call-leave', {
      props: {
        callId,
      },
    });
    return result;
  };

  const disconnect = (callId: string) => {
    return socket.value?.timeout(3000).emitWithAck<SocketEventType>('call-disconnect', {
      props: {
        callId,
      },
    });
  };

  const joinRequest = async (
    callId: string,
  ): Promise<SocketEventResponse<'response-call-join-request'>> => {
    return socket.value?.timeout(3000).emitWithAck<SocketEventType>('call-join-request', {
      props: {
        callId,
      },
    });
  };

  const withdrawJoinRequest = async (callId: string) => {
    return socket.value?.timeout(3000).emitWithAck<SocketEventType>('call-withdraw-join-request', {
      props: {
        callId,
      },
    });
  };

  const approveJoinRequest = async (callId: string, patientId: string) => {
    const response: SocketEventResponse<'response-call-approve-join-request'> = await socket.value
      ?.timeout(1000)
      .emitWithAck<SocketEventType>('call-approve-join-request', {
        props: {
          callId,
          patientId,
        },
      });
    return response;
  };

  const callFileUpload = async (callId: string) => {
    return socket.value?.timeout(3000).emitWithAck<SocketEventType>('call-file-upload', {
      props: {
        callId,
      },
    });
  };

  const kickUser = async (userId: string, callId: string) => {
    return socket.value?.timeout(3000).emitWithAck<SocketEventType>('call-kick-user', {
      props: {
        callId,
        userId,
      },
    });
  };

  const connect = async (): Promise<SocketEventResponse<'response-call-status'>> => {
    // reconnect handled in callReconnectStore
    return new Promise((resolve) => {
      const callSessionToken = getCallSessionToken();
      socket.value = io(
        socketEndpoint,
        callSessionToken
          ? getCallSessionSocketConfig(callSessionToken)
          : getDefaulSocketConfig(
              authStore.authContext?.token,
              getActiveOfficeId(),
              getActiveCaregiverId(),
            ),
      );

      socket.value.on('call-join', (event: SocketEvent<'call-join'>) => {
        callEventStore.emit(CallEventType.SOCKET_IO, 'call-join', event);
        joinTrigger.next(event);
      });

      socket.value.on('call-leave', (event: SocketEvent<'call-leave'>) => {
        callEventStore.emit(CallEventType.SOCKET_IO, 'call-leave', event);
        leaveTrigger.next(event);
      });

      socket.value.on('call-disconnect', (event: SocketEvent<'call-disconnect'>) => {
        callEventStore.emit(CallEventType.SOCKET_IO, 'call-disconnect', event);
        callDisconnectTrigger.next(event);
      });

      socket.value.on(
        'call-ended-by-therapist',
        (event: SocketEvent<'call-ended-by-therapist'>) => {
          callEventStore.emit(CallEventType.SOCKET_IO, 'call-ended-by-therapist', event);
          callEndedByTherapistTrigger.next(event);
        },
      );

      socket.value.on('call-user-got-kicked', (event: SocketEvent<'call-user-got-kicked'>) => {
        callEventStore.emit(CallEventType.SOCKET_IO, 'call-user-got-kicked', event);
        callUserGotKickedTrigger.next(event);
      });

      socket.value.on('call-join-request', (event: SocketEvent<'call-join-request'>) => {
        callEventStore.emit(CallEventType.SOCKET_IO, 'call-join-request', event);
        const callRequest = new CallRequest();
        callRequest.user = event.props.from;
        callRequest.callId = event.props.callId;
        callRequest.officeId = event.props.officeId ?? null;
        callRequests.value.set(callRequest.user.id + '', callRequest);
        callRequestTrigger.next(event);
      });

      socket.value.on(
        'call-withdraw-join-request',
        (event: SocketEvent<'call-withdraw-join-request'>) => {
          callEventStore.emit(CallEventType.SOCKET_IO, 'call-withdraw-join-request', event);
          callRequests.value.delete(event.props.from.id + '');
        },
      );

      socket.value.on(
        'call-approve-join-request',
        (event: SocketEvent<'call-approve-join-request'>) => {
          callEventStore.emit(CallEventType.SOCKET_IO, 'call-approve-join-request', event);
          callConnectTrigger.next(event);
        },
      );

      socket.value.on('call-file-upload', (event: SocketEvent<'call-file-upload'>) => {
        callEventStore.emit(CallEventType.SOCKET_IO, 'call-file-upload', event);
        fileUploadTrigger.next();
      });

      socket.value.on('disconnect', (reason, details) => {
        callEventStore.emit(CallEventType.SOCKET_IO, 'disconnect', { reason, details });
        logger.warn({ msg: 'Socket.IO disconnected', reason });
      });

      socket.value.on('error', (error) => {
        callEventStore.emit(CallEventType.SOCKET_IO, 'error', { error });
        logger.error({ msg: 'Socket.IO error', error });
      });

      socket.value.on('exception', (error) => {
        callEventStore.emit(CallEventType.SOCKET_IO, 'exception', { error });
        logger.error({ msg: 'Socket.IO exception', error });
      });

      socket.value.on('connect', async () => {
        callEventStore.emit(CallEventType.SOCKET_IO, 'connect');
        const statusResponse = await status();
        resolve(statusResponse);
      });

      socket.value.connect();
    });
  };

  return {
    join,
    leave,
    disconnect,
    approveJoinRequest,
    joinRequest,
    connect,
    callRequests,
    nextCallRequest,
    callConnectTrigger,
    joinTrigger,
    leaveTrigger,
    withdrawJoinRequest,
    status,
    callFileUpload,
    fileUploadTrigger,
    callIdInServerStatus,
    callDisconnectTrigger,
    disconnectSocket,
    callRequestTrigger,
    callEndedByTherapistTrigger,
    callUserGotKickedTrigger,
    kickUser,
  };
});
